/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { CluGptPredictedUtteranceStore } from 'src/clu/core/stores/CluGptPredictedUtteranceStore';
import { CluUtteranceEntityLabel, CluUtteranceStore } from 'src/clu/core/stores/CluUtteranceStore';
import { utteranceStoreToUtteranceSwagger } from 'src/clu/modules/handlers/utterances/cluUtteranceMappers';
import { LabelingStore } from 'src/luis/projects/utterances/stores/LabelingStore';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';
import { UtteranceFiltersStore } from 'src/verticals/utterancesFilters/stores/UtteranceFiltersStore';

export type SavingState = 'dirty' | 'saved' | 'inProgress';
export type CLuTagUtterancesPageSidePane = 'entityPalette' | 'filters' | 'utterancesGptGeneration' | 'orchestrationDistribution';
export type ModalsConfigState = {
    isMoveUtterancesModalHidden: boolean;
    isDeleteUtterancesModalHidden: boolean;
    isUploadUtterancesModalHidden: boolean;
    createNewIntentModal: { isHidden: boolean; initialIntentName: string };
};

export class CluTagUtterancesUiStore {
    @observable public rawUtterancesJson: CluUtteranceSwagger[];

    @observable public labelingStore: LabelingStore;

    @observable public selectedDataset: DatasetType;

    @observable public filtersStore: UtteranceFiltersStore;

    @observable public displayedUtterancesStores: (CluUtteranceStore | CluGptPredictedUtteranceStore)[];

    @observable public searchQuery: string;

    @observable public selectedEntityId: string;

    @observable public selectedIntentId: string;

    @observable public savingState: SavingState;

    @observable public utteranceToEditId: number;

    @observable public modalsConfigState: ModalsConfigState;

    @observable public activeSidePane: CLuTagUtterancesPageSidePane;

    @observable public entityDetailsBladeConfigState: { isOpen: boolean; entityId: string };

    constructor(rawUtterances: CluUtteranceSwagger[], projectType: CluProjectType, selectedDataset?: 'Train' | 'Test') {
        this.searchQuery = '';
        this.selectedEntityId = '';
        this.savingState = 'saved';
        this.utteranceToEditId = null;
        this.selectedDataset = selectedDataset ?? 'Train';
        this.displayedUtterancesStores = [];
        this.rawUtterancesJson = Array.from(rawUtterances);
        this.labelingStore = new LabelingStore();
        this.filtersStore = new UtteranceFiltersStore();
        this.entityDetailsBladeConfigState = { isOpen: false, entityId: '' };
        this.activeSidePane = projectType === 'Conversation' ? 'entityPalette' : 'orchestrationDistribution';
        this.modalsConfigState = {
            isMoveUtterancesModalHidden: true,
            isUploadUtterancesModalHidden: true,
            isDeleteUtterancesModalHidden: true,
            createNewIntentModal: { isHidden: true, initialIntentName: '' }
        };
    }

    @action
    public setRawUtterancesJson(rawUtterancesJson: CluUtteranceSwagger[]) {
        this.rawUtterancesJson = Array.from(rawUtterancesJson);
    }

    @action
    public setSelectedDataset(selectedDataset: DatasetType) {
        this.selectedDataset = selectedDataset;
    }

    @action
    public setSavingState(state: SavingState) {
        this.savingState = state;
    }

    @action
    public setSearchQuery(query: string) {
        this.searchQuery = query;
    }

    @action
    public setActiveSidePane(sidePane: CLuTagUtterancesPageSidePane) {
        this.activeSidePane = sidePane;
    }

    @action
    public setSelectedEntityId(entityId: string) {
        this.selectedEntityId = entityId;
    }

    @action
    public setSelectedIntentId(intentId: string) {
        this.selectedIntentId = intentId;
    }

    @action
    public setUtteranceToEditId(utteranceId: number) {
        this.utteranceToEditId = utteranceId;
    }

    @action
    public setDisplayedUtteranceStores(displayedUtteranceStores: CluUtteranceStore[]) {
        this.displayedUtterancesStores = Array.from(displayedUtteranceStores);
    }

    @action
    public addDisplayedUtteranceStore(utteranceData: { labeledIntentName: string; language: string; text: string }) {
        const newStore = new CluUtteranceStore({
            ...utteranceData,
            dataset: this.selectedDataset,
            tokenizedText: utteranceData.text.split('')
        });
        const indexToAddTo = this.rawUtterancesJson.filter(u => u.dataset === 'Generated').length;
        this.rawUtterancesJson.splice(indexToAddTo, 0, utteranceStoreToUtteranceSwagger(newStore));
        this.displayedUtterancesStores.splice(indexToAddTo, 0, newStore);

        return newStore;
    }

    @action
    public editDisplayedUtteranceStore(utteranceId: number, newData: Pick<CluUtteranceStore, 'language' | 'labeledIntentName' | 'text'>) {
        const utteranceStore = this.displayedUtterancesStores.find(utterance => utterance.id === utteranceId);
        const swagger = this.rawUtterancesJson.find(u => u.text === utteranceStore.text);
        const isGeneratedUtterance = utteranceStore.dataset === 'Generated';

        if (isGeneratedUtterance) {
            utteranceStore.setLanguage(newData.language);
            if (
                utteranceStore.text !== newData.text ||
                ((utteranceStore as CluGptPredictedUtteranceStore).updatedText &&
                    (utteranceStore as CluGptPredictedUtteranceStore).updatedText !== newData.text)
            ) {
                (utteranceStore as CluGptPredictedUtteranceStore).setUpdatedText(newData.text);
                (utteranceStore as CluGptPredictedUtteranceStore).setGenerationDecision('Modified');
            }

            if (utteranceStore.labeledIntentName !== newData.labeledIntentName) {
                (utteranceStore as CluGptPredictedUtteranceStore).setPredictionDecision('Modified');
                (utteranceStore as CluGptPredictedUtteranceStore).setUpdatedLabeledIntentName(newData.labeledIntentName);
            }

            return;
        }

        utteranceStore.setLabeledIntent(newData.labeledIntentName);
        utteranceStore.setLanguage(newData.language);
        utteranceStore.setText(newData.text);

        swagger.text = utteranceStore.text;
        swagger.intent = utteranceStore.labeledIntentName;
        swagger.language = utteranceStore.language;

        if (newData.text && newData.text !== utteranceStore.text) {
            utteranceStore.setLabeledEntities([]);
            swagger.entities = [];
        }
    }

    @action
    public reassignDisplayedUtteranceStores(utteranceIds: number[], labeledIntentName: string) {
        utteranceIds.forEach(utteranceId => {
            const utteranceStore = this.displayedUtterancesStores.find(utterance => utterance.id === utteranceId);
            utteranceStore.setLabeledIntent(labeledIntentName);
            const indexToUpdate = this.rawUtterancesJson.findIndex(u => u.text === utteranceStore.text);
            this.rawUtterancesJson[indexToUpdate] = utteranceStoreToUtteranceSwagger(utteranceStore);
        });
    }

    @action
    public redistributeDisplayedUtteranceStores(utteranceIds: number[], dataset: DatasetType) {
        utteranceIds.forEach(utteranceId => {
            const utteranceStore = this.displayedUtterancesStores.find(utterance => utterance.id === utteranceId);
            utteranceStore.setDataset(dataset);
            const indexToUpdate = this.rawUtterancesJson.findIndex(u => u.text === utteranceStore.text);
            this.rawUtterancesJson[indexToUpdate] = utteranceStoreToUtteranceSwagger(utteranceStore);
            this.rawUtterancesJson = this.rawUtterancesJson.sort(a => (a.dataset === 'Generated' ? -1 : 1));
        });
    }

    @action
    public removeDisplayedUtteranceStores(utteranceIds: number[], dataset?: DatasetType) {
        const utteranceTextsToRemove = new Set<string>();
        const utterancesToKeep: CluUtteranceStore[] = [];

        this.displayedUtterancesStores.forEach(utteranceStore => {
            if (utteranceIds.includes(utteranceStore.id)) {
                utteranceTextsToRemove.add(utteranceStore.text);
            } else {
                utterancesToKeep.push(utteranceStore);
            }
        });

        this.displayedUtterancesStores = utterancesToKeep;
        const utteranceSwaggersToKeep = this.rawUtterancesJson.filter(u =>
            dataset
                ? !(utteranceTextsToRemove.has(u.text) && (typeof u.dataset === 'undefined' ? dataset === 'Train' : u.dataset === dataset))
                : !utteranceTextsToRemove.has(u.text)
        );

        this.rawUtterancesJson = utteranceSwaggersToKeep;
    }

    @action
    public updateDisplayedUtteranceStoreLabels(utteranceId: number, newLabels: CluUtteranceEntityLabel[]) {
        const utteranceStore = this.displayedUtterancesStores.find(store => store.id === utteranceId);
        utteranceStore.setLabeledEntities(newLabels);

        const indexToUpdate = this.rawUtterancesJson.findIndex(
            u =>
                u.text === utteranceStore.text &&
                u.intent === utteranceStore.labeledIntentName &&
                (u.dataset ?? 'Train') === utteranceStore.dataset
        );
        this.rawUtterancesJson[indexToUpdate] = utteranceStoreToUtteranceSwagger(utteranceStore);
    }

    @action
    public setModalsConfigState(configs: Partial<ModalsConfigState>) {
        this.modalsConfigState = { ...this.modalsConfigState, ...configs };
    }

    @action
    public setEntityDetailsBladeConfigState(configs: Partial<{ isOpen: boolean; entityId: string }>) {
        this.entityDetailsBladeConfigState = { ...this.entityDetailsBladeConfigState, ...configs };
    }
}
